import React, { useRef, useState } from 'react';
import { IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonContent, IonText } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { Html5Qrcode } from 'html5-qrcode';

const SCANNER_ELEMENT_ID = 'domiot-qr-scanner';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onScan: (decodedText: string) => void;
}

const QrScannerModal: React.FC<Props> = ({ isOpen, onClose, onScan }) => {
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  // Se llama recién cuando el modal terminó de animarse y su contenido ya
  // está en el DOM — arrancar antes (ej. en un efecto atado a `isOpen`)
  // hace que Html5Qrcode no encuentre el <div> todavía y tire error.
  const handlePresent = async () => {
    setError(null);

    // getUserMedia (la cámara) solo funciona en "contexto seguro": HTTPS, o
    // localhost/127.0.0.1. Un IP de red local por HTTP (ej. al probar desde
    // el celular en desarrollo) no cuenta — ahí ni aparece el permiso.
    if (!window.isSecureContext) {
      setError(
        'El navegador no deja usar la cámara en esta conexión (necesita HTTPS, o localhost). ' +
          'Probá desde la app ya desplegada, o desde la compu en localhost.'
      );
      return;
    }

    const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
    scannerRef.current = scanner;

    try {
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: 240 },
        (decodedText) => onScanRef.current(decodedText),
        () => {
          // Se llama en cada frame sin código detectado — no es un error real.
        }
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo acceder a la cámara.');
    }
  };

  // Antes de que arranque la animación de cierre, para la cámara — así el
  // indicador de "usando la cámara" del navegador se apaga al toque.
  const handleWillDismiss = async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (!scanner) return;
    try {
      await scanner.stop();
      scanner.clear();
    } catch {
      // El scanner puede no haber llegado a arrancar (ej. permiso denegado).
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidPresent={handlePresent} onWillDismiss={handleWillDismiss} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Escanear código</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div id={SCANNER_ELEMENT_ID} style={{ width: '100%' }} />
        {error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}
      </IonContent>
    </IonModal>
  );
};

export default QrScannerModal;
